import { useState, useRef, useEffect } from 'preact/hooks'
import { ChevronDown, Building, Check } from 'lucide-preact'
import { THEME_COLORS } from '../../constants/theme'

export interface CompanySummary {
  id: string
  name: string
}

interface CompanySelectorProps {
  selectedCompanyId: string
  onCompanyChange: (companyId: string) => void
  companies: CompanySummary[]
}

export function CompanySelector({ selectedCompanyId, onCompanyChange, companies }: CompanySelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!companies || companies.length === 0) {
    return (
      <div className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">
        No companies available
      </div>
    )
  }

  // If only one company, show it as a simple label
  if (companies.length === 1) {
    const company = companies[0]
    return (
      <div
        className="flex items-center px-3 py-2 rounded-lg border sm:min-w-48 max-w-full"
        style={{
          backgroundColor: THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE,
          borderColor: THEME_COLORS.BORDERS.LIGHT
        }}
      >
        <Building className="w-4 h-4 mr-2" style={{ color: THEME_COLORS.GREEN[500] }} />
        <span className="font-medium truncate text-sm" style={{ color: THEME_COLORS.GREEN[600] }}>
          {company.name}
        </span>
      </div>
    )
  }

  // Multiple companies - show dropdown selector
  const selectedCompany = companies.find(c => c.id === selectedCompanyId) || companies[0]

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between px-3 py-2 text-sm rounded-lg transition-colors duration-200 sm:min-w-48 max-w-full border hover:opacity-90"
        style={{
          backgroundColor: THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE,
          borderColor: THEME_COLORS.BORDERS.LIGHT
        }}
      >
        <div className="flex items-center min-w-0">
          <Building className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: THEME_COLORS.GREEN[500] }} />
          <span className="font-medium truncate" style={{ color: THEME_COLORS.GREEN[600] }}>
            {selectedCompany.name}
          </span>
        </div>
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''
            }`}
          style={{ color: THEME_COLORS.GREEN[500] }}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto sm:min-w-48 max-w-full">
          {companies.map((company) => {
            const isSelected = company.id === selectedCompany.id
            return (
              <button
                key={company.id}
                onClick={() => {
                  onCompanyChange(company.id)
                  setIsOpen(false)
                }}
                className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors duration-200 first:rounded-t-lg last:rounded-b-lg hover:bg-slate-50 dark:hover:bg-slate-700`}
                style={{
                  backgroundColor: isSelected ? THEME_COLORS.BACKGROUNDS.LIGHT_SUBTLE : 'transparent'
                }}
              >
                <div className="flex items-center min-w-0">
                  <Building
                    className={`w-4 h-4 mr-2 flex-shrink-0 ${isSelected ? '' : 'text-slate-400 dark:text-slate-500'
                      }`}
                    style={{ color: isSelected ? THEME_COLORS.GREEN[500] : undefined }}
                  />
                  <div className="text-left min-w-0">
                    <div
                      className={`font-medium truncate ${isSelected ? '' : 'text-slate-700 dark:text-slate-300'
                        }`}
                      style={{ color: isSelected ? THEME_COLORS.GREEN[600] : undefined }}
                    >
                      {company.name}
                    </div>
                  </div>
                </div>
                {isSelected && (
                  <Check className="w-4 h-4 flex-shrink-0" style={{ color: THEME_COLORS.GREEN[500] }} />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
